// Burst coalescer for file_changed: collapses bulk-git-op storms per directory.
// Design: packages/workspaces/_devlog/notes/2026-06-20-file-changed-coalescer-design.md

import type { ConnectorChangeEvent } from "@skaile/workspaces/connectors";

/** Tuning for {@link FileChangedCoalescer}. */
export interface CoalesceOptions {
  /**
   * Burst window in ms. Events within this gap of each other (per mount) are
   * considered part of the same burst. Default: 1000.
   */
  windowMs?: number;
  /**
   * Number of events within a window that flips a mount into "burst mode".
   * At/under this count events pass through individually; above it the rest of
   * the burst is coalesced by directory. Default: 50.
   */
  threshold?: number;
  /**
   * Cap on the number of per-directory representative events emitted per burst.
   * If a burst touches more distinct directories than this, the representatives
   * for the first `maxDirs` directories are emitted plus a single root-level
   * fallback event so nothing is silently missed. Default: 256.
   */
  maxDirs?: number;
}

const DEFAULT_WINDOW_MS = 1000;
const DEFAULT_THRESHOLD = 50;
const DEFAULT_MAX_DIRS = 256;

/** Connector wire actions that change a directory's listing (add/remove entry). */
function isStructuralAction(action: ConnectorChangeEvent["action"]): boolean {
  return action === "create" || action === "delete";
}

/** Parent directory of a mount-relative path; top-level entries parent to root (""). */
function parentDirOf(path: string): string {
  return path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
}

/**
 * Representative chosen for a single affected directory during a burst. We keep
 * the first event seen for the dir, and — if any structural change occurred in
 * that dir — separately remember a structural event so the emitted
 * representative carries a structural action (real path inside the dir). This
 * keeps the backend filter forwarding it to expanded-folder watchers and makes
 * the frontend refetch the grandparent too.
 */
interface DirRepresentative {
  first: ConnectorChangeEvent;
  structural: ConnectorChangeEvent | null;
}

interface MountBurstState {
  /** Timestamp of the most recent event. */
  lastEvent: number;
  /** Events seen in the current window. */
  count: number;
  /** True once `count` has exceeded the threshold — burst mode. */
  bursting: boolean;
  /** One representative per distinct parent directory touched while bursting. */
  dirs: Map<string, DirRepresentative>;
  /** True once `dirs` overflowed `maxDirs` — emit a coarse root fallback on flush. */
  dirCapHit: boolean;
  /** Pending flush timer that emits the representatives once the burst quiesces. */
  timer: ReturnType<typeof setTimeout> | null;
}

/**
 * Per-mount burst detector. Feed it watcher events via {@link push}; it calls
 * `emit` either with the original event (normal traffic) or with one
 * representative event per affected directory (when a burst is detected).
 * Quiescence is detected with a timer so representatives fire shortly after the
 * last file in the burst settles.
 *
 * Stateful and not thread-safe — one instance per `startWatching` lifetime.
 */
export class FileChangedCoalescer {
  private readonly windowMs: number;
  private readonly threshold: number;
  private readonly maxDirs: number;
  private readonly emit: (mountId: string, event: ConnectorChangeEvent) => void;
  private readonly now: () => number;
  private readonly log: (msg: string) => void;
  private readonly states = new Map<string, MountBurstState>();

  constructor(
    emit: (mountId: string, event: ConnectorChangeEvent) => void,
    options?: CoalesceOptions,
    // Injectable clock for deterministic tests.
    now: () => number = Date.now,
    // Injectable logger for the cap-hit warning.
    log: (msg: string) => void = (m) => console.warn(m),
  ) {
    this.emit = emit;
    this.windowMs = options?.windowMs ?? DEFAULT_WINDOW_MS;
    this.threshold = options?.threshold ?? DEFAULT_THRESHOLD;
    this.maxDirs = options?.maxDirs ?? DEFAULT_MAX_DIRS;
    this.now = now;
    this.log = log;
  }

  push(mountId: string, event: ConnectorChangeEvent): void {
    const t = this.now();
    let state = this.states.get(mountId);
    if (!state) {
      state = {
        lastEvent: t,
        count: 0,
        bursting: false,
        dirs: new Map(),
        dirCapHit: false,
        timer: null,
      };
      this.states.set(mountId, state);
    }

    // A gap longer than the window ends the previous burst and starts fresh.
    if (t - state.lastEvent > this.windowMs) {
      this.flush(mountId, state); // also resets bursting/dirs
      state.count = 0;
    }
    state.lastEvent = t;
    state.count += 1;

    if (state.bursting) {
      this.recordDir(state, event);
      this.armFlush(mountId, state);
      return;
    }

    if (state.count > this.threshold) {
      // Crossing the threshold: enter burst mode. Everything from here until
      // quiescence collapses by directory. The already-forwarded sub-threshold
      // events are harmless (each is a real change); the per-dir representatives
      // cover the rest of the burst.
      state.bursting = true;
      this.recordDir(state, event);
      this.armFlush(mountId, state);
      return;
    }

    // Below threshold — stream through unchanged.
    this.emit(mountId, event);
  }

  /** Cancel timers and emit any pending representatives. Call on watcher teardown. */
  dispose(): void {
    for (const [mountId, state] of this.states) this.flush(mountId, state);
    this.states.clear();
  }

  private recordDir(state: MountBurstState, event: ConnectorChangeEvent): void {
    const dir = parentDirOf(event.path);
    let rep = state.dirs.get(dir);
    if (!rep) {
      if (state.dirs.size >= this.maxDirs) {
        // Too many distinct dirs to represent individually; the root fallback
        // emitted on flush covers the overflow.
        state.dirCapHit = true;
        return;
      }
      rep = { first: event, structural: null };
      state.dirs.set(dir, rep);
    }
    if (!rep.structural && isStructuralAction(event.action)) {
      rep.structural = event;
    }
  }

  private armFlush(mountId: string, state: MountBurstState): void {
    if (state.timer) clearTimeout(state.timer);
    state.timer = setTimeout(() => {
      const s = this.states.get(mountId);
      if (s) this.flush(mountId, s);
    }, this.windowMs);
    // Don't keep the process alive for a pending flush.
    (state.timer as { unref?: () => void }).unref?.();
  }

  private flush(mountId: string, state: MountBurstState): void {
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }
    if (state.bursting && state.dirs.size > 0) {
      // Prefer a structural representative per dir (so the backend forwards it to
      // expanded-folder watchers and the frontend refetches the grandparent too);
      // otherwise the first event seen for the dir (an edit) is fine.
      for (const rep of state.dirs.values()) {
        this.emit(mountId, rep.structural ?? rep.first);
      }
      if (state.dirCapHit) {
        this.log(
          `[file-changed-coalescer] mount ${mountId}: burst touched more than ${this.maxDirs} directories; emitted ${this.maxDirs} per-dir events + a root fallback`,
        );
        // Coarse fallback: a root-relative event so a consumer at least refreshes
        // the mount root for the dirs we couldn't represent individually.
        this.emit(mountId, { path: "", action: "edit", source: "filesystem" });
      }
    }
    state.bursting = false;
    state.dirs.clear();
    state.dirCapHit = false;
  }
}
